import { type FetchResult, Observable, type Operation } from "@apollo/client";
import { ApolloLink } from "@apollo/client/link/core/ApolloLink";
import {
  type FrontendSentrySpan,
  getSentryTraceHeaders,
  withFrontendSentrySpan,
} from "@atoms/sentry-module/frontend";

type SpanStatus = "ok" | "error" | "cancelled";

export const makeSentryTraceLink = () =>
  new ApolloLink((operation, forward) => {
    const operationName = operation.operationName || "anonymous";

    return withFrontendSentrySpan(
      `graphql.${operationName}`,
      {
        op: "graphql.client",
        attributes: {
          operationName,
        },
      },
      (span) => {
        return span.withActive(() => {
          injectSentryTraceHeaders(operation);
          return trackSentrySpan(forward(operation), span);
        });
      },
    );
  });

function injectSentryTraceHeaders(operation: Operation): void {
  const sentryHeaders = getSentryTraceHeaders();
  if (Object.keys(sentryHeaders).length === 0) {
    return;
  }

  operation.setContext(({ headers }: { headers?: Record<string, string> }) => ({
    headers: {
      ...headers,
      ...sentryHeaders,
    },
  }));
}

function trackSentrySpan(
  observable: Observable<FetchResult>,
  span: FrontendSentrySpan,
): Observable<FetchResult> {
  return new Observable<FetchResult>((observer) => {
    const spanFinisher = createSpanFinisher(span);
    const subscription = span.withActive(() =>
      observable.subscribe({
        next: (value) => observer.next(value),
        error: (error) => {
          spanFinisher("error");
          observer.error(error);
        },
        complete: () => {
          spanFinisher("ok");
          observer.complete();
        },
      }),
    );

    return () => {
      span.withActive(() => {
        spanFinisher("cancelled");
        subscription.unsubscribe();
      });
    };
  });
}

function createSpanFinisher(
  span: FrontendSentrySpan,
): (status: SpanStatus) => void {
  let finished = false;

  return (status: SpanStatus) => {
    if (finished) return;
    finished = true;
    span.setData("status", status);
    span.end();
  };
}
